// v4.10 - TRUST-KPI-01: tenant-wide "Data Reliability" badge - 2026-04-28
//
// Different concept than ConfidenceBadge:
//   ConfidenceBadge   = how sure are we about THIS specific number?
//   DataReliability   = how trustworthy is the underlying data overall?
//
// Renders next to the Dashboard heading. Shows Strong / Mixed / Weak with
// a tooltip listing source counts and any collusion findings. Hidden when
// no reliability summary is provided (flag off, or pre-trust pipelines).
import React from 'react';
import { Chip, Tooltip, Stack, Typography } from '@mui/material';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import type { TenantReliability } from '../services/api';

type ChipColor = 'success' | 'warning' | 'error' | 'default';

const LABEL_COLOR: Record<TenantReliability['label'], ChipColor> = {
  Strong: 'success',
  Mixed: 'warning',
  Weak: 'error',
};

const LABEL_HEADLINE: Record<TenantReliability['label'], string> = {
  Strong: 'Your data sources are working well together.',
  Mixed: 'Some of your data sources disagree with each other.',
  Weak: 'Your data sources show significant conflicts.',
};

export interface DataReliabilityBadgeProps {
  reliability: TenantReliability | null | undefined;
}

const DataReliabilityBadge: React.FC<DataReliabilityBadgeProps> = ({
  reliability,
}) => {
  if (!reliability) return null;

  const color = LABEL_COLOR[reliability.label] || 'default';
  const tooltip = (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {LABEL_HEADLINE[reliability.label]}
      </Typography>
      <Typography variant="caption">
        {reliability.sources_count} source
        {reliability.sources_count === 1 ? '' : 's'} connected
      </Typography>
      {reliability.suppressed_count > 0 && (
        <Typography variant="caption">
          {reliability.suppressed_count} record
          {reliability.suppressed_count === 1 ? '' : 's'} flagged as less reliable
        </Typography>
      )}
      {reliability.collusion_count > 0 && (
        <Typography variant="caption">
          {reliability.collusion_count} potential duplicate
          {reliability.collusion_count === 1 ? '' : 's'} between sources
        </Typography>
      )}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} arrow placement="bottom-end">
      <Chip
        size="small"
        icon={<ShieldRoundedIcon fontSize="small" />}
        label={`Data reliability: ${reliability.label}`}
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ fontWeight: 500 }}
        aria-label={`Tenant data reliability: ${reliability.label}`}
      />
    </Tooltip>
  );
};

export default DataReliabilityBadge;
